import type { RouteLocationNormalized } from 'vue-router'
import type { Nullable } from '~/types'

/**
 * Hooks into the router so that after each route change we:
 * 1. update the page context and track non-Segment page views right away
 *    (Datadog must start its view as early as possible);
 * 2. send the Segment page view once the head has been rendered for the new page,
 *    so the title is the new page's title, not the previous one's.
 *
 * Also fires on initial load.
 */
export default defineNuxtPlugin((nuxtApp) => {
  const runtimeConfig = useRuntimeConfig()
  const router = useRouter()

  let pending: Nullable<{ to: RouteLocationNormalized, from: RouteLocationNormalized }> = null
  let fallbackTimer: ReturnType<typeof setTimeout> | undefined

  const flushSegmentPage = () => {
    if (!pending) {
      return
    }

    pending = null
    clearTimeout(fallbackTimer)

    finalizePageContext()
    trackSegmentPageViewed(runtimeConfig.public)
  }

  router.afterEach((to, from, failure) => {
    if (failure) return

    setPageContextFromRoute(to, from)
    trackPageViewed({ $config: runtimeConfig.public, to, from }) // Datadog, Clarity, OpenAI only

    pending = { to, from }

    // Safety net in case no head render happens for this navigation
    // (e.g. query-only change, or an identical title).
    clearTimeout(fallbackTimer)
    fallbackTimer = setTimeout(flushSegmentPage, 2000)
  })

  // Head updates are held back while a page is loading and written once it's done.
  // Waiting for the render guarantees document.title belongs to the new page.
  nuxtApp.vueApp.runWithContext(() => {
    useHead({}, {
      onRendered: flushSegmentPage,
    })
  })
})
